// src/components/family/ChildCardSkeleton.tsx
// Loading skeleton for ChildCard component

interface ChildCardSkeletonProps {
  variant?: 'grid' | 'list';
}

export function ChildCardSkeleton({ variant = 'grid' }: ChildCardSkeletonProps) {
  if (variant === 'list') {
    return (
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-warm-gray/10 animate-pulse">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-full bg-cream" />

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-5 bg-cream rounded w-32" />
          <div className="h-4 bg-cream rounded w-20" />
        </div>

        {/* Badge */}
        <div className="hidden sm:block">
          <div className="h-6 bg-cream rounded-full w-24" />
        </div>

        {/* Actions */}
        <div className="hidden md:flex items-center gap-2">
          <div className="w-8 h-8 bg-cream rounded-lg" />
          <div className="w-8 h-8 bg-cream rounded-lg" />
          <div className="w-8 h-8 bg-cream rounded-lg" />
        </div>

        {/* Menu */}
        <div className="w-8 h-8 bg-cream rounded-lg" />
      </div>
    );
  }

  // Grid variant (default)
  return (
    <div className="bg-white rounded-2xl border border-warm-gray/10 overflow-hidden animate-pulse">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex flex-col items-center">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-cream mb-3" />
          
          {/* Name */}
          <div className="h-6 bg-cream rounded w-28 mb-2" />
          
          {/* Age */}
          <div className="h-4 bg-cream rounded w-20" />
        </div>
      </div>

      {/* Diagnoses */}
      <div className="px-6 pb-4">
        <div className="flex flex-wrap justify-center gap-2">
          <div className="h-6 bg-cream rounded-full w-20" />
          <div className="h-6 bg-cream rounded-full w-16" />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-warm-gray/10" />

      {/* Quick Actions */}
      <div className="grid grid-cols-3 divide-x divide-warm-gray/10">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col items-center py-3">
            <div className="w-4 h-4 bg-cream rounded mb-1" />
            <div className="w-10 h-3 bg-cream rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton grid for multiple loading cards
 */
export function ChildCardSkeletonGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <ChildCardSkeleton key={i} variant="grid" />
      ))}
    </div>
  );
}

/**
 * Skeleton list for multiple loading items
 */
export function ChildCardSkeletonList({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <ChildCardSkeleton key={i} variant="list" />
      ))}
    </div>
  );
}

export default ChildCardSkeleton;
